// Decyzon — single source of truth for ACTION-NATIVE language.
// Every UI surface (Feed, Result page, History, Shared) renders one of:
//   BUY  ·  WATCH — forming  ·  WATCH — no entry  ·  AVOID — overpriced  ·  AVOID — late
// Internal labels (STRONG / CONSIDER / WAIT / PASS / efficient) NEVER reach the UI.
//
// Each card must answer:
//   1. What to do            → state + primaryLine
//   2. Why                   → reason (1 short sentence)
//   3. What breaks it        → invalidator (1 short sentence)
//   4. How to execute        → entryLabel · exitLabel · stopLabel  (BUY only)
//   5. How long it's valid   → validFor                            (BUY only)
//   6. Liquidity reality     → bookLabel + liquidity-aware downgrade

import { subjectFromQuestion } from "./predictionSubject";

export type ActionState =
  | "BUY"
  | "WATCH_FORMING"
  | "WATCH_NO_ENTRY"
  | "WATCH_ENTRY_PASSED"
  | "WATCH_NO_EDGE"
  | "WATCH_BOOK_THIN"
  | "WATCH_NO_PROFIT_ROOM"
  | "WATCH_SNAPSHOT_ONLY"
  | "AVOID_OVERPRICED"
  | "AVOID_LATE";
export type ActionVerb = "BUY" | "WATCH" | "AVOID";
export type Conviction = "high" | "medium" | "low";
export type DecisionStyle = "conservative" | "balanced" | "aggressive";

export interface ActionView {
  state: ActionState;
  verb: ActionVerb;
  /** "BUY YES ≤ 42¢" · "WATCH — forming" · "AVOID — overpriced" */
  primaryLine: string;
  /** Short option label, never null for BUY. */
  optionLabel: string | null;
  /** Price ceiling formatted as "≤ 42¢" or null. */
  ceilingLabel: string | null;
  /** Entry / Exit / Stop — populated for BUY only. */
  entryLabel: string | null;   // "≤ 42¢"
  exitLabel: string | null;    // "≥ 55¢"
  stopLabel: string | null;    // "≤ 36¢"
  /** "Valid 2h" · "Valid 12h" · "Valid 24h" — BUY only. */
  validFor: string | null;
  /** "Book $1.2k" — null when liquidity unknown. */
  bookLabel: string | null;
  /** Optional one-line personal adjust ("For aggressive: hold to ≥ 60¢"). */
  personalLine: string | null;
  /** One-sentence rationale rendered under the primary line. */
  reason: string;
  /** One-sentence invalidator — what breaks this read. */
  invalidator: string;
  /** Optional entry-context note (BUY only). */
  entryContext: string | null;
  /** Visual conviction (●●● / ●●○ / ●○○) — never expressed as a word. */
  conviction: Conviction;
  /** Color tone token. */
  tone: "primary" | "foreground" | "accent" | "muted";
}

export interface SourceOptionLike {
  label: string;
  market_probability?: number | null;
  estimated_probability?: number | null;
  edge_pct?: number | null;
}

interface ActionInput {
  decision: string | null | undefined;            // "STRONG" | "CONSIDER" | "WAIT" | "PASS"
  edge_label: string | null | undefined;          // "underpriced" | "overpriced" | "efficient"
  edge_pct: number | null | undefined;            // signed, e.g. 0.08
  market_probability: number | null | undefined;  // 0-1, CURRENT price
  /** Price at which the read was originally formed. Used for edge-taken gate. */
  last_market_probability?: number | null;        // 0-1
  estimated_probability?: number | null;          // 0-1 — drives exit price
  forced_winner?: string | null;
  side?: string | null;
  best_option?: string | null;
  too_expensive_above?: string | null;            // "≤ 42¢" or "0.42"
  entry_zone?: string | null;                     // "0.36–0.42"
  invalidation?: string | null;
  recheck_when?: string | null;
  why?: string | null;
  why_short?: string | null;
  trap_label?: string | null;
  trap?: string | null;
  delta_24h?: number | null;
  delta_1h?: number | null;
  end_date?: string | null;
  confidence?: string | null;                     // "low" | "medium" | "high"
  market_question?: string | null;
  /** Optional concrete angle ("what the market is missing"). */
  angle?: string | null;
  /** Liquidity inputs — drive slippage haircut + book label. */
  total_liquidity?: number | null;
  total_volume?: number | null;
  /** Sibling-divergence (cross-market mispricing) — used for fallback reason. */
  sibling_divergence?: number | null;
  velocity_ratio?: number | null;
  /** Personal style — adds at most ONE optional line. Never alters verdict. */
  decision_style?: DecisionStyle | null;
  /** Source-truth options/outcomes. Visible side labels must match one of these. */
  options?: SourceOptionLike[] | null;
}

// --- helpers ---------------------------------------------------------------

function normalizeOptionLabel(label: string | null | undefined): string | null {
  if (typeof label !== "string") return null;
  const trimmed = label.replace(/\s+/g, " ").trim();
  return trimmed.length > 0 ? trimmed : null;
}

function sourceOptions(i: ActionInput): SourceOptionLike[] {
  if (!Array.isArray(i.options)) return [];
  return i.options
    .map((opt) => ({ ...opt, label: normalizeOptionLabel(opt?.label) ?? "" }))
    .filter((opt) => opt.label.length > 0);
}

function pickOption(i: ActionInput): string | null {
  const options = sourceOptions(i);
  if (options.length === 0) return null;

  const labels = new Map(options.map((opt) => [opt.label.toLowerCase(), opt.label]));
  const candidates = [i.forced_winner, i.best_option, i.side]
    .map((label) => normalizeOptionLabel(label))
    .filter((label): label is string => !!label);

  for (const candidate of candidates) {
    const match = labels.get(candidate.toLowerCase());
    if (match) return decorateYesNo(match, i.market_question);
  }
  return null;
}

/** When the matched source label is literally "Yes"/"No", append a short
 *  question-derived subject so the rendered side reads "Yes — Sunderland wins"
 *  instead of a bare "Yes". Falls back to the bare label when no clean
 *  subject can be derived. */
function decorateYesNo(label: string, question: string | null | undefined): string {
  const t = label.replace(/\s+/g, " ").trim().toLowerCase();
  if (t !== "yes" && t !== "no") return label;
  const subject = subjectFromQuestion(question);
  if (!subject) return label;
  const suffix = t === "no" ? `not ${subject}` : subject;
  return `${label} — ${suffix}`;
}

function formatPrice(raw: string | null | undefined, fallbackProb?: number | null): string | null {
  if (raw && raw.trim()) {
    const t = raw.trim();
    if (/^[≤<≥>]/.test(t)) return t;
    const num = parseFloat(t.replace(/[^\d.]/g, ""));
    if (Number.isFinite(num)) {
      const cents = num <= 1 ? Math.round(num * 100) : Math.round(num);
      return `≤ ${cents}¢`;
    }
    return t;
  }
  if (fallbackProb != null && fallbackProb > 0 && fallbackProb < 1) {
    return `≤ ${Math.round(fallbackProb * 100)}¢`;
  }
  return null;
}

function shortFirstSentence(s: string | null | undefined, max = 110): string | null {
  if (!s) return null;
  const trimmed = s.replace(/\s+/g, " ").trim();
  if (!trimmed) return null;
  const dot = trimmed.search(/[.!?](\s|$)/);
  const cut = dot > 20 ? trimmed.slice(0, dot + 1) : trimmed;
  if (cut.length <= max) return cut;
  return cut.slice(0, max - 1).trimEnd() + "…";
}

function ppText(edge: number | null | undefined): string | null {
  if (edge == null || !Number.isFinite(edge)) return null;
  const v = Math.round(edge * 100);
  if (v === 0) return null;
  return `${v > 0 ? "+" : ""}${v}pp`;
}

function daysToEnd(end_date: string | null | undefined): number | null {
  if (!end_date) return null;
  const ms = Date.parse(end_date) - Date.now();
  if (!Number.isFinite(ms)) return null;
  return ms / 86400000;
}

/** Slippage haircut in PROBABILITY UNITS (e.g. 0.015 = 1.5pp).
 *  We only have aggregate (two-sided) liquidity. Conservative rule: assume the
 *  side we're buying holds at most ~40% of total → effectively halve the budget,
 *  then map to a haircut. Unknown liquidity → treat as thin (3pp). */
function slippageHaircut(liq: number | null | undefined): number {
  if (liq == null || !Number.isFinite(liq)) return 0.03; // unknown → thin
  const sideBudget = liq * 0.4; // conservative single-side estimate
  if (sideBudget >= 5000) return 0.01;
  if (sideBudget >= 1000) return 0.02;
  if (sideBudget >= 400) return 0.03;
  return 0.05; // very thin → big haircut
}

function bookLabel(liq: number | null | undefined): string | null {
  if (liq == null || !Number.isFinite(liq) || liq <= 0) return null;
  if (liq >= 1000) return `Book $${(liq / 1000).toFixed(liq >= 10000 ? 0 : 1)}k`;
  return `Book $${Math.round(liq)}`;
}

/** "Valid 2h" / "Valid 12h" / "Valid 24h" based on velocity. */
function validForLabel(i: ActionInput): string {
  const d1 = Math.abs(i.delta_1h ?? 0);
  const dte = daysToEnd(i.end_date);
  if (d1 >= 0.02) return "Valid 2h";
  if (dte != null && dte > 60) return "Valid 24h";
  return "Valid 12h";
}

function clampCents(v: number): number {
  return Math.max(1, Math.min(99, Math.round(v)));
}

/** Exit price (take profit). MUST clear entry + slippage + 5pp profit buffer.
 *  Otherwise the trade exits inside the slippage band → realized loss on a
 *  winning thesis. If fair value can't support that floor we return null and
 *  the caller blocks the BUY. */
function deriveExit(
  estProb: number | null | undefined,
  marketProb: number | null | undefined,
  haircut: number,
): string | null {
  const fair = estProb ?? null;
  const mp = marketProb ?? null;
  if (fair == null || mp == null) return null;
  const profitBuffer = 0.05;
  const floor = mp + haircut + profitBuffer;       // entry + slippage + 5pp
  const fairTarget = fair - 0.05;                  // leave 5pp before fair
  // Take the LARGER of the two so exit always clears the slippage band.
  const target = Math.max(floor, fairTarget);
  if (target >= 0.99) return null; // no realistic room
  return `≥ ${clampCents(target * 100)}¢`;
}

/** Stop price — exits if move goes against the thesis. */
function deriveStop(marketProb: number | null | undefined): string | null {
  if (marketProb == null) return null;
  return `≤ ${clampCents(marketProb * 100 - 5)}¢`;
}

function personalAdjust(
  style: DecisionStyle | null | undefined,
  estProb: number | null | undefined,
  marketProb: number | null | undefined,
): string | null {
  if (!style || style === "balanced") return null;
  const fair = estProb ?? null;
  const mp = marketProb ?? null;
  if (fair == null || mp == null) return null;
  if (style === "aggressive") {
    // Hold longer — push exit toward fair value
    const target = clampCents(Math.min(fair + 0.02, 0.95) * 100);
    return `For aggressive: hold to ≥ ${target}¢`;
  }
  // conservative — tighter exit
  const target = clampCents(Math.max(mp + 0.02, fair - 0.08) * 100);
  return `For conservative: trim at ≥ ${target}¢`;
}

// --- core derivation -------------------------------------------------------

export function deriveAction(i: ActionInput): ActionView {
  const decision = (i.decision ?? "WAIT").toUpperCase();
  const edgeLabel = (i.edge_label ?? "").toLowerCase();
  const rawEdge = i.edge_pct ?? 0;
  const dte = daysToEnd(i.end_date);
  const option = pickOption(i);
  const isLate = dte != null && dte < 2;
  const book = bookLabel(i.total_liquidity);

  // ---- AVOID branch
  if (decision === "PASS" || edgeLabel === "overpriced" || rawEdge <= -0.02) {
    if (isLate) {
      const moveText = (() => {
        const d24 = i.delta_24h ?? 0;
        if (Math.abs(d24) >= 0.05) return `Late entry — already moved ${d24 >= 0 ? "+" : ""}${Math.round(d24 * 100)}pp.`;
        return null;
      })();
      const reason = moveText ?? shortFirstSentence(i.trap ?? i.why_short ?? i.why) ?? "Resolves too soon. No entry trigger left.";
      return {
        state: "AVOID_LATE", verb: "AVOID",
        primaryLine: option ? `Trap — too late on ${option}` : "Trap — too late to enter",
        optionLabel: option, ceilingLabel: null,
        entryLabel: null, exitLabel: null, stopLabel: null,
        validFor: null, bookLabel: book, personalLine: null,
        reason,
        invalidator: shortFirstSentence(i.invalidation) ?? "Reopens only on a sharp price reset.",
        entryContext: null, conviction: "high", tone: "accent",
      };
    }
    const moveText = (() => {
      const d24 = i.delta_24h ?? 0;
      if (Math.abs(d24) >= 0.05) return `Already moved ${d24 >= 0 ? "+" : ""}${Math.round(d24 * 100)}pp in 24h.`;
      const ppt = ppText(rawEdge);
      return ppt && Math.abs(rawEdge) >= 0.04
        ? `Market overpriced by ~${Math.abs(Math.round(rawEdge * 100))}pp.`
        : null;
    })();
    // STRICT: AVOID needs a real reason. No vague fallback.
    const reason = shortFirstSentence(i.trap) ?? moveText ?? shortFirstSentence(i.why_short ?? i.why);
    if (!reason) {
      // No specific reason → downgrade to WATCH_NO_EDGE (clearer sub-state).
      return {
        state: "WATCH_NO_EDGE", verb: "WATCH",
        primaryLine: "No edge at this price",
        optionLabel: option, ceilingLabel: null,
        entryLabel: null, exitLabel: null, stopLabel: null,
        validFor: null, bookLabel: book, personalLine: null,
        reason: "Price matches our read. Most losses happen here.",
        invalidator: shortFirstSentence(i.invalidation) ?? "Re-check on >3pp move.",
        entryContext: null, conviction: "low", tone: "muted",
      };
    }
    return {
      state: "AVOID_OVERPRICED", verb: "AVOID",
      primaryLine: option ? `Trap — avoid ${option} at current price` : "Trap — avoid at current price",
      optionLabel: option, ceilingLabel: null,
      entryLabel: null, exitLabel: null, stopLabel: null,
      validFor: null, bookLabel: book, personalLine: null,
      reason,
      invalidator: shortFirstSentence(i.invalidation) ?? "Re-check on >5pp pullback.",
      entryContext: null, conviction: "high", tone: "accent",
    };
  }

  // ---- BUY branch — strict, executable, liquidity-aware

  const hasAngle = !!(i.angle && i.angle.trim().length > 0);
  const haircut = slippageHaircut(i.total_liquidity);
  const executableEdge = rawEdge - haircut;

  // STRICT REASON GATE — block math-only BUYs entirely.
  // A BUY ships only when at least ONE of these is true:
  //   • angle text exists (cross-platform / sibling / catalyst)
  //   • sharp 1h move (≥ 2pp) — book hasn't caught up
  //   • sibling divergence ≥ 5pp — mispriced vs correlated markets
  //   • velocity spike (ratio > 1.5) — volume up, price lagging
  //   • raw edge ≥ 8pp AND delta_24h ≥ 3pp (strong gap with movement)
  const d1 = i.delta_1h ?? 0;
  const d24 = i.delta_24h ?? 0;
  const sib = i.sibling_divergence ?? 0;
  const vel = i.velocity_ratio ?? 0;
  const sharpMove = Math.abs(d1) >= 0.02;
  const siblingMispricing = Math.abs(sib) >= 0.05;
  const velocitySpike = vel > 1.5;
  const strongGapWithMove = rawEdge >= 0.08 && Math.abs(d24) >= 0.03;
  const hasRealReason = hasAngle || sharpMove || siblingMispricing || velocitySpike || strongGapWithMove;

  const buyEligible =
    (decision === "STRONG" || decision === "CONSIDER") &&
    (edgeLabel === "underpriced" || rawEdge >= 0.03) &&
    rawEdge >= 0.03 &&
    (i.confidence ?? "medium") !== "low";

  // Liquidity-aware: only ship BUY when edge survives the haircut.
  const buyExecutable = executableEdge >= 0.03;

  // EDGE-TAKEN GATE: if current price has already crossed the entry ceiling,
  // OR the market moved ≥3pp against us since the read was formed, the BUY
  // is stale. Never ship a BUY the user can no longer execute at our price.
  const ceilingProb = (() => {
    const raw = i.too_expensive_above;
    if (!raw) return null;
    const t = String(raw).trim();
    const num = parseFloat(t.replace(/[^\d.]/g, ""));
    if (!Number.isFinite(num)) return null;
    return num <= 1 ? num : num / 100;
  })();
  const mp = i.market_probability;
  const lastMp = i.last_market_probability;
  const ceilingBreached = ceilingProb != null && mp != null && mp > ceilingProb;
  const movedSinceScan = mp != null && lastMp != null ? mp - lastMp : 0;
  const edgeTaken = ceilingBreached || movedSinceScan >= 0.03;

  if (buyEligible && hasRealReason && buyExecutable && !edgeTaken) {
    const ceiling = formatPrice(i.too_expensive_above, i.market_probability);
    const exit = deriveExit(i.estimated_probability, i.market_probability, haircut);
    // Profitability gate: if exit can't clear entry+slippage+5pp, demote.
    if (!exit) {
      return {
        state: "WATCH_NO_PROFIT_ROOM", verb: "WATCH",
        primaryLine: "Edge real — no clean exit at this price",
        optionLabel: option, ceilingLabel: ceiling,
        entryLabel: null, exitLabel: null, stopLabel: null,
        validFor: null, bookLabel: book, personalLine: null,
        reason: "Fair value too close to entry — exit would land inside slippage.",
        invalidator: "Re-check on >3pp pullback.",
        entryContext: null, conviction: "medium", tone: "foreground",
      };
    }
    const conviction: Conviction =
      hasAngle && (decision === "STRONG" || rawEdge >= 0.06) ? "high" :
      decision === "STRONG" && rawEdge >= 0.06 ? "high" : "medium";

    const reason =
      shortFirstSentence(i.angle) ??
      (sharpMove
        ? `Sharp ${d1 >= 0 ? "+" : ""}${Math.round(d1 * 100)}pp / 1h move — book hasn't caught up.`
        : siblingMispricing
        ? `Mispriced vs correlated markets by ${Math.abs(Math.round(sib * 100))}pp.`
        : velocitySpike
        ? "Volume spiking, price lagging."
        : strongGapWithMove
        ? `Underpriced by ~${Math.round(rawEdge * 100)}pp with confirming move.`
        : shortFirstSentence(i.why_short) ?? shortFirstSentence(i.why) ?? "Setup confirmed.");

    const buyLine = (() => {
      const opt = option;
      if (opt && ceiling) return `BUY ${opt} ${ceiling}`;
      if (opt) return `BUY ${opt}`;
      if (ceiling) return `BUY ${ceiling}`;
      return "BUY";
    })();

    return {
      state: "BUY", verb: "BUY",
      primaryLine: buyLine,
      optionLabel: option, ceilingLabel: ceiling,
      entryLabel: ceiling,
      exitLabel: exit,
      stopLabel: deriveStop(i.market_probability),
      validFor: validForLabel(i),
      bookLabel: book,
      personalLine: personalAdjust(i.decision_style, i.estimated_probability, i.market_probability),
      reason,
      invalidator: shortFirstSentence(i.invalidation) ?? `Above ${ceiling ? ceiling.replace("≤ ", "") : "current range"} — edge gone.`,
      entryContext: i.entry_zone ? `Entry zone ${i.entry_zone}` : null,
      conviction, tone: "primary",
    };
  }

  // Edge already taken → explicit, honest downgrade.
  if (buyEligible && hasRealReason && buyExecutable && edgeTaken) {
    return {
      state: "WATCH_ENTRY_PASSED", verb: "WATCH",
      primaryLine: "Entry passed — wait for pullback",
      optionLabel: option,
      ceilingLabel: formatPrice(i.too_expensive_above, i.market_probability),
      entryLabel: null, exitLabel: null, stopLabel: null,
      validFor: null, bookLabel: book, personalLine: null,
      reason: ceilingBreached
        ? "Price already crossed our entry — chasing now pays the crowd."
        : `Moved ${Math.round(movedSinceScan * 100)}pp since scan — re-read needed.`,
      invalidator: "Re-check after a pullback or fresh scan.",
      entryContext: null, conviction: "medium", tone: "foreground",
    };
  }

  // BUY-eligible but blocked → show WHY it didn't ship, in trader voice.
  if (buyEligible && hasRealReason && !buyExecutable) {
    const slipPp = Math.round(haircut * 100);
    return {
      state: "WATCH_BOOK_THIN", verb: "WATCH",
      primaryLine: "Edge real — liquidity too thin to act",
      optionLabel: option,
      ceilingLabel: formatPrice(i.too_expensive_above, i.market_probability),
      entryLabel: null, exitLabel: null, stopLabel: null,
      validFor: null, bookLabel: book, personalLine: null,
      reason: `Edge real but book too thin — would slip ~${slipPp}pp.`,
      invalidator: "Re-check when liquidity > $1k.",
      entryContext: null, conviction: "medium", tone: "foreground",
    };
  }

  // ---- WATCH branch — split: forming (movement/early signal) vs no-entry (idle)
  const hasMovement =
    Math.abs(i.delta_24h ?? 0) >= 0.02 ||
    Math.abs(i.delta_1h ?? 0) >= 0.01 ||
    rawEdge >= 0.02;

  if (hasMovement || hasAngle) {
    const ppt = ppText(rawEdge);
    const moveText = (() => {
      const dd = i.delta_24h ?? 0;
      if (Math.abs(dd) >= 0.02) return `Moving ${dd >= 0 ? "+" : ""}${Math.round(dd * 100)}pp / 24h — close to entry.`;
      return ppt ? `Prediction stands; entry not hit yet (${ppt}).` : "Prediction stands; entry not hit.";
    })();
    return {
      state: "WATCH_FORMING", verb: "WATCH",
      primaryLine: "WATCH — entry trigger not hit yet",
      optionLabel: option,
      ceilingLabel: formatPrice(i.too_expensive_above, i.market_probability),
      entryLabel: null, exitLabel: null, stopLabel: null,
      validFor: null, bookLabel: book, personalLine: null,
      reason: shortFirstSentence(i.angle) ?? shortFirstSentence(i.why_short) ?? moveText,
      invalidator: shortFirstSentence(i.recheck_when) ?? shortFirstSentence(i.invalidation) ?? "Re-check on >3pp move.",
      entryContext: null, conviction: "medium", tone: "foreground",
    };
  }

  return {
    state: "WATCH_NO_ENTRY", verb: "WATCH",
    primaryLine: "WATCH — no entry trigger yet",
    optionLabel: option, ceilingLabel: null,
    entryLabel: null, exitLabel: null, stopLabel: null,
    validFor: null, bookLabel: book, personalLine: null,
    reason: shortFirstSentence(i.why_short) ?? shortFirstSentence(i.why) ?? "Crowd hasn't given a clean price yet.",
    invalidator: shortFirstSentence(i.recheck_when) ?? "Re-check on >3pp move.",
    entryContext: null, conviction: "low", tone: "muted",
  };
}

// --- visual helpers --------------------------------------------------------

export function convictionDots(c: Conviction): string {
  if (c === "high") return "●●●";
  if (c === "medium") return "●●○";
  return "●○○";
}

export function verbBadgeClass(verb: ActionVerb): string {
  if (verb === "BUY") return "bg-primary text-primary-foreground border-primary";
  if (verb === "AVOID") return "bg-accent/15 text-accent border-accent/40";
  return "bg-muted/40 text-foreground border-border";
}

export function verbAccentClass(verb: ActionVerb): string {
  if (verb === "BUY") return "text-primary";
  if (verb === "AVOID") return "text-accent";
  return "text-foreground";
}
